import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, Menu, X } from "lucide-react";

const LINKS = [
  { id: "#work", label: "Work", testid: "nav-link-work" },
  { id: "#films", label: "Films", testid: "nav-link-films" },
  { id: "#about", label: "About", testid: "nav-link-about" },
  { id: "#services", label: "Services", testid: "nav-link-services" },
  { id: "#contact", label: "Contact", testid: "nav-link-contact" },
];

export const Navbar = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (id) => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-ink/85 border-b border-ivory/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-5 sm:px-10 h-[76px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo.jpeg"
            alt="ANSH AGARWAL FILMS logo"
            data-testid="nav-logo-image"
            className="w-11 h-11 object-cover rounded-sm ring-1 ring-ivory/20 group-hover:ring-amber/60 transition-all duration-500"
          />
          <span className="hidden sm:block font-display text-sm tracking-[0.3em] text-ivory">
            ANSH AGARWAL
            <span className="block text-[9px] tracking-[0.55em] text-ash mt-0.5">FILMS</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="relative font-mono text-[11px] tracking-[0.3em] uppercase text-ash hover:text-ivory transition-colors duration-300 after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-amber hover:after:w-full after:transition-all after:duration-500"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            data-testid="nav-admin-login-button"
            aria-label="Admin login"
            className="w-10 h-10 flex items-center justify-center border border-line text-ash hover:text-amber hover:border-amber/50 transition-all duration-300"
          >
            <Lock size={14} />
          </Link>
          <button
            data-testid="nav-book-shoot-button"
            onClick={() => go("#contact")}
            className="hidden sm:block font-mono text-[11px] tracking-[0.25em] uppercase bg-ivory text-ink px-6 py-3 hover:bg-amber transition-colors duration-300"
          >
            Book a Shoot
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            aria-label="Menu"
            onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 flex items-center justify-center border border-line text-ivory"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-ink/95 border-b border-ivory/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.testid}`}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-lg tracking-[0.2em] uppercase text-cream hover:text-amber transition-colors"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
